// BE status codes (as returned from API)
export const STATUS_CODES = {
  DRAFT:              'DRAFT',
  SUBM:               'SUBM',
  SUBM_ERR:           'SUBM_ERR',
  CONTRL_ACK:         'CONTRL_ACK',
  CONTRL_NACK:        'CONTRL_NACK',
  ACT:                'ACT',
  ACW:                'ACW',
  DNL:                'DNL',
  EXP:                'EXP',
  PSF:                'PSF',
  RFI:                'RFI',
  CANCEL_CONTRL_ACK:  'CANCEL_CONTRL_ACK',
  CANCEL_CONTRL_NACK: 'CANCEL_CONTRL_NACK',
  UPDATE_DRAFT:       'UPDATE_DRAFT',
  ATTACH_ACK:         'ATTACH_ACK',
  ATTACH_NACK:        'ATTACH_NACK',
  PROC:               'PROC'
}

// Code → human-readable label
export const STATUS_LABEL = {
  DRAFT:              'New Draft',
  SUBM:               'Submitted to NAIC',
  SUBM_ERR:           'Error in Submission',
  CONTRL_ACK:         'Successful Acknowledgment',
  CONTRL_NACK:        'Failed Acknowledgement',
  ACT:                'Assessment Complete',
  ACW:                'Assessment Complete with Warning',
  DNL:                'Do Not Load',
  EXP:                'Filing Expiry',
  PSF:                'Pending Sub-filing',
  RFI:                'Request for Information',
  CANCEL_CONTRL_ACK:  'Cancellation Successful',
  CANCEL_CONTRL_NACK: 'Cancellation Failed',
  UPDATE_DRAFT:       'Updated Filing Draft Created',
  ATTACH_ACK:         'Attachment Successfully Received by NAIC',
  ATTACH_NACK:        'Attached Submission to NAIC is unsuccessful',
  PROC:               'Processing'
}

// Code → chip color
export const STATUS_COLOR = {
  DRAFT:              { bg: '#e3f2fd', text: '#1565c0' },
  SUBM:               { bg: '#e8f5e9', text: '#2e7d32' },
  SUBM_ERR:           { bg: '#fce4ec', text: '#c62828' },
  CONTRL_ACK:         { bg: '#e8f5e9', text: '#2e7d32' },
  CONTRL_NACK:        { bg: '#fce4ec', text: '#c62828' },
  ACT:                { bg: '#e8f5e9', text: '#2e7d32' },
  ACW:                { bg: '#fff8e1', text: '#f57f17' },
  DNL:                { bg: '#fce4ec', text: '#c62828' },
  EXP:                { bg: '#fafafa', text: '#616161' },
  PSF:                { bg: '#fff8e1', text: '#f57f17' },
  RFI:                { bg: '#fff3e0', text: '#e65100' },
  CANCEL_CONTRL_ACK:  { bg: '#f3e5f5', text: '#6a1b9a' },
  CANCEL_CONTRL_NACK: { bg: '#fce4ec', text: '#c62828' },
  UPDATE_DRAFT:       { bg: '#e3f2fd', text: '#1565c0' },
  ATTACH_ACK:         { bg: '#e8f5e9', text: '#2e7d32' },
  ATTACH_NACK:        { bg: '#fce4ec', text: '#c62828' },
  PROC:               { bg: '#e8eaf6', text: '#3949ab' }
}

// Sub-status codes for statuses that have variants (SUBM_ERR, CONTRL_NACK)
export const SUB_STATUS_CODES = {
  FD: 'FD',  // First Draft
  CI: 'CI',  // Connectivity Issue
  SD: 'SD'   // Subsequent Draft (Latest Draft)
}

export const SUB_STATUS_LABEL = {
  FD: 'First Draft',
  CI: 'Connectivity Issue',
  SD: 'Subsequent Draft'
}

export function getSubStatusLabel(code) {
  return SUB_STATUS_LABEL[code] || code || null
}

// Statuses that require supporting documents when submitting an update
export const STATUSES_REQUIRE_ATTACHMENT = [
  'ACT', 'ACW', 'DNL', 'RFI'
]

// Statuses where the Update Filing flow applies (not plain draft edit)
export const STATUSES_UPDATE_MODE = [
  'SUBM', 'SUBM_ERR', 'CONTRL_ACK', 'CONTRL_NACK',
  'ACT', 'ACW', 'DNL', 'EXP', 'PSF', 'RFI',
  'CANCEL_CONTRL_ACK', 'CANCEL_CONTRL_NACK',
  'UPDATE_DRAFT', 'ATTACH_ACK', 'ATTACH_NACK'
]

// Helper: get label for a code (fallback to raw code)
export function getStatusLabel(code) {
  return STATUS_LABEL[code] || code || '—'
}

// Statuses where the Update action is available outside the SUBM_ERR/CONTRL_NACK special-case below
const UPDATE_ELIGIBLE_STATUSES = [
  STATUS_CODES.UPDATE_DRAFT,
  STATUS_CODES.CONTRL_ACK, STATUS_CODES.ACT, STATUS_CODES.ACW,
  STATUS_CODES.DNL, STATUS_CODES.EXP, STATUS_CODES.PSF, STATUS_CODES.RFI,
  STATUS_CODES.CANCEL_CONTRL_NACK,
  STATUS_CODES.ATTACH_ACK, STATUS_CODES.ATTACH_NACK
]

// Can the Update Filing (and, via its Function Type dropdown, Cancellation) action be initiated for this record?
export function canUpdateFiling(status, subStatus) {
  if (status === STATUS_CODES.SUBM_ERR || status === STATUS_CODES.CONTRL_NACK) {
    return subStatus === SUB_STATUS_CODES.SD
  }
  return UPDATE_ELIGIBLE_STATUSES.includes(status)
}

// Can the latest saved draft be opened for editing (Failed Ack / Error in Submission, except connectivity issue)?
export function canEditDraft(status, subStatus) {
  if (status === STATUS_CODES.SUBM_ERR || status === STATUS_CODES.CONTRL_NACK) {
    return subStatus === SUB_STATUS_CODES.FD || subStatus === SUB_STATUS_CODES.SD
  }
  return [STATUS_CODES.DRAFT, STATUS_CODES.UPDATE_DRAFT].includes(status)
}

// Legacy default export (kept for backward compat)
const BILL_STATUSES = {
  SUBMITTED_TO_NAIC:          STATUS_LABEL.SUBM,
  SUCCESSFUL_ACK:             STATUS_LABEL.CONTRL_ACK,
  FAILED_ACK:                 STATUS_LABEL.CONTRL_NACK,
  ASSESSMENT_COMPLETE:        STATUS_LABEL.ACT,
  ASSESSMENT_COMPLETE_WARNING:STATUS_LABEL.ACW,
  DO_NOT_LOAD:                STATUS_LABEL.DNL,
  FILING_EXPIRY:              STATUS_LABEL.EXP,
  REQUEST_FOR_INFO:           STATUS_LABEL.RFI,
  CANCELLATION_SUCCESS:       STATUS_LABEL.CANCEL_CONTRL_ACK,
  CANCELLATION_FAILED:        STATUS_LABEL.CANCEL_CONTRL_NACK,
  ERROR_IN_SUBMISSION:        STATUS_LABEL.SUBM_ERR
}

export default BILL_STATUSES
